// The section menu of a BasePage (templates/website/base_page.html): scrolls
// smoothly to the section picked, marks the section being read, and on small
// screens opens and closes as a dropdown; there, further down the page,
// scrolling up also brings the site's navbar back, pinned to the top.
export default class SectionMenu {
  constructor(selector = '.section-menu') {
    this.menu = document.querySelector(selector)
    if (!this.menu) return

    this.toggle = this.menu.querySelector('.section-menu-toggle')
    this.current = this.menu.querySelector('.section-menu-current')
    this.links = [...this.menu.querySelectorAll('.section-menu-link')]
    this.headings = this.links
      .map((link) => document.getElementById(link.hash.slice(1)))
      .filter(Boolean)
    // While scrolling to a picked section, keep it marked instead of every
    // section passed on the way.
    this.scrollingTo = null

    this.navbar = document.querySelector('body > .has-background-navbar')
    this.lastScrollY = window.scrollY
    this.measureNavbar()
    window.addEventListener('resize', () => this.measureNavbar())

    this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen()))
    this.links.forEach((link) =>
      link.addEventListener('click', (e) => this.go(e, link)),
    )
    // Clicking elsewhere or pressing Escape closes the dropdown.
    document.addEventListener('click', (e) => {
      if (!this.menu.contains(e.target)) this.setOpen(false)
    })
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.setOpen(false)
    })

    window.addEventListener(
      'scroll',
      () => {
        this.pinNavbar()
        this.markCurrent()
      },
      { passive: true },
    )
    window.addEventListener('scrollend', () => this.endScroll())
    this.markCurrent()
  }

  isOpen() {
    return this.menu.classList.contains('is-open')
  }

  setOpen(open) {
    this.menu.classList.toggle('is-open', open)
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
  }

  go(e, link) {
    const heading = document.getElementById(link.hash.slice(1))
    if (!heading) return

    e.preventDefault()
    this.setOpen(false)
    this.mark(link)
    this.scrollingTo = link
    // Even when going back up, a pinned navbar would cover the section title.
    this.setNavbarPinned(false)
    // Browsers without scrollend: stop holding the mark after a while.
    if (!('onscrollend' in window)) {
      clearTimeout(this.scrollTimeout)
      this.scrollTimeout = setTimeout(() => this.endScroll(), 1500)
    }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    // scrollIntoView honours the headings' scroll-margin-top (the sticky bar).
    heading.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
    history.pushState(null, '', link.hash)
  }

  endScroll() {
    if (!this.scrollingTo) return
    this.scrollingTo = null
    this.lastScrollY = window.scrollY
    this.markCurrent()
  }

  isNavbarPinned() {
    return document.body.classList.contains('is-navbar-pinned')
  }

  // Pinning slides the navbar in, pushing the section dropdown down with it
  // when that is stuck to the top; unpinning plays the same backwards before
  // letting go (immediately with animate=false, or where nothing animates).
  setNavbarPinned(pinned, animate = true) {
    const body = document.body
    const wasPinned = this.isNavbarPinned()
    const leaving = body.classList.contains('is-navbar-unpinning')

    if (pinned) {
      body.classList.remove('is-navbar-unpinning')
      if (!wasPinned) {
        this.menu.classList.toggle('is-pushed', this.menu.getBoundingClientRect().top <= 1)
        body.classList.add('is-navbar-pinned')
      }
      return
    }

    if (!wasPinned || (leaving && animate)) return
    const animated =
      animate &&
      window.matchMedia('(max-width: 1023px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!animated) {
      body.classList.remove('is-navbar-unpinning', 'is-navbar-pinned')
      return
    }

    const stuckTop = this.menu.getBoundingClientRect().top
    this.menu.classList.toggle('is-pushed', Math.abs(stuckTop - this.navbarHeight) <= 1)
    body.classList.add('is-navbar-unpinning')
    const done = () => {
      if (!body.classList.contains('is-navbar-unpinning')) return
      body.classList.remove('is-navbar-unpinning', 'is-navbar-pinned')
    }
    this.navbar.addEventListener('animationend', done, { once: true })
    // In case animationend never comes (the animation was cut short).
    setTimeout(done, 400)
  }

  // Where the navbar sits in the page (measured while it is not pinned) and
  // its height, which the styles use to hold its place and to put the section
  // dropdown below it.
  measureNavbar() {
    if (!this.navbar) return
    if (!this.isNavbarPinned()) {
      this.navbarTop = this.navbar.getBoundingClientRect().top + window.scrollY
    }
    this.navbarHeight = this.navbar.offsetHeight
    document.documentElement.style.setProperty(
      '--navbar-height',
      `${this.navbarHeight}px`,
    )
  }

  // Scrolling up once the navbar is out of sight pins it to the top (styled on
  // small screens only); scrolling down, or back up to where it sits in the
  // page, unpins it. A few pixels of slack keep it from flickering.
  pinNavbar() {
    // A modal holding the page in place scrolls it to 0 and back: not a move.
    if (document.body.classList.contains('is-scroll-locked')) return
    if (!this.navbar || this.scrollingTo) return
    const y = window.scrollY
    const delta = y - this.lastScrollY
    if (y <= this.navbarTop) {
      // Back where it sits in the page: let go without moving it.
      this.setNavbarPinned(false, false)
    } else if (Math.abs(delta) < 8) {
      return
    } else if (delta > 0) {
      this.setNavbarPinned(false)
    } else if (y > this.navbarTop + this.navbarHeight || this.isNavbarPinned()) {
      this.setNavbarPinned(true)
    }
    this.lastScrollY = y
  }

  // The current section is the last one whose title has scrolled past the
  // top third of the screen (the first one before any has).
  markCurrent() {
    if (this.scrollingTo) return

    const line = window.innerHeight / 3
    let index = 0
    this.headings.forEach((heading, i) => {
      if (heading.getBoundingClientRect().top <= line) index = i
    })
    const heading = this.headings[index]
    const link = heading && this.links.find((l) => l.hash === `#${heading.id}`)
    if (link) this.mark(link)
  }

  mark(current) {
    this.links.forEach((link) => {
      const active = link === current
      link.classList.toggle('is-active', active)
      if (active) link.setAttribute('aria-current', 'location')
      else link.removeAttribute('aria-current')
    })
    this.current.textContent = current.textContent
  }
}
